import { makeStyles } from "tss-react/mui";

import { DEAD_ACCOUNT_FUNERAL_BG_URL } from "@/constants/deadAccount";

const funeralBackground = `linear-gradient(180deg, rgba(0, 0, 0, 0.8) 60%, rgba(0, 0, 0, 1) 90%), url(${DEAD_ACCOUNT_FUNERAL_BG_URL})`;

const styles = makeStyles()((theme) => ({
  screen: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "48px 24px",
    minHeight: "min(70vh, 640px)",
    boxSizing: "border-box",
    backgroundImage: funeralBackground,
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    borderRadius: 16,
    border: "1px solid rgba(196, 75, 75, 0.25)",
    marginBottom: 32,

    [theme.breakpoints.down("sm")]: {
      padding: "32px 16px",
      minHeight: "auto",
    },
  },

  screenFullscreen: {
    width: "100%",
    minHeight: "100%",
    marginBottom: 0,
    borderRadius: 0,
    border: "none",
    backgroundImage: funeralBackground,
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "scroll",
    padding: "64px 24px",

    [theme.breakpoints.down("sm")]: {
      padding: "48px 16px",
      minHeight: "100%",
      backgroundPosition: "center top",
    },
  },

  icon: {
    width: 80,
    height: 80,
    objectFit: "contain",
    marginBottom: 16,
    opacity: 0.9,
    filter: "grayscale(0.3)",
  },

  title: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 48,
    fontWeight: 900,
    color: "#e8e8e8",
    textTransform: "uppercase",
    letterSpacing: 2,
    marginBottom: 12,
    textShadow: "0 0 40px rgba(196, 75, 75, 0.5)",

    [theme.breakpoints.down("sm")]: {
      fontSize: 32,
    },
  },

  subtitle: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 16,
    fontWeight: 600,
    color: "#b0b0b0",
    maxWidth: 520,
    lineHeight: 1.5,
    marginBottom: 24,
  },

  killerCard: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    maxWidth: 420,
    padding: "16px 20px",
    marginBottom: 28,
    borderRadius: 12,
    boxSizing: "border-box",
    textAlign: "center",
  },

  killerCardTitle: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "#ff8a8a",
    textTransform: "uppercase",
    letterSpacing: 1,
  },

  killerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },

  killerLabel: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 13,
    fontWeight: 600,
    color: "#888",
  },

  killerValue: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "white",
  },

  killerLink: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "white",
    textDecoration: "none",

    "&:hover": {
      color: "#ff8a8a",
      textDecoration: "underline",
    },
  },

  killerResultSingle: {
    color: "#8fd48f",
  },

  killerResultDouble: {
    color: "#f5d76e",
  },

  killerResultBackfire: {
    color: "#ff8a8a",
  },

  killerMessage: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginTop: 4,
    paddingTop: 12,
    borderTop: `1px solid ${theme.design.color.overlay.white10}`,
  },

  killerMessageLabel: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 12,
    fontWeight: 700,
    color: theme.design.color.status.danger,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },

  killerMessageText: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 15,
    fontWeight: 600,
    color: theme.design.color.text.primary,
    lineHeight: 1.5,
    overflowWrap: "anywhere",
  },

  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
    width: "100%",
    maxWidth: 640,
  },

  primaryButton: {
    flex: 1,
    minWidth: 200,
    background: "linear-gradient(135deg, #c44b4b, #6b1515)",
    color: "white",
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    padding: "14px 28px",
    borderRadius: 8,
    textTransform: "none",
    border: "1px solid rgba(0,0,0,0.4)",

    "&:hover": {
      background: "linear-gradient(135deg, #d65a5a, #7a1a1a)",
    },
  },

  secondaryButton: {
    flex: 1,
    minWidth: 180,
    backgroundColor: "rgb(37, 39, 40)",
    color: "white",
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    padding: "14px 28px",
    borderRadius: 8,
    textTransform: "none",
    border: "1px solid #000",

    "&:hover": {
      backgroundColor: "rgb(50, 55, 56)",
    },
  },

  tertiaryButton: {
    flex: "1 1 100%",
    minWidth: 200,
    maxWidth: 400,
    backgroundColor: "transparent",
    color: "#c0c0c0",
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 15,
    fontWeight: 700,
    padding: "12px 24px",
    borderRadius: 8,
    textTransform: "none",
    border: "1px solid rgba(255, 255, 255, 0.2)",

    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
      borderColor: "rgba(255, 255, 255, 0.35)",
    },
  },

  restrictionNote: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 12,
    color: "#888",
    marginTop: 24,
    maxWidth: 480,
    lineHeight: 1.4,
  },
}));

export default styles;
